import { BasicButton, Button } from '../../../../widgets/buttons';
import { Checkbox } from '../../../../widgets/checkbox';
import { PageFooterButtons } from '../../../../widgets/headers';
import { QuestionDialog, useDialogManager } from '../../../../widgets/dialog';
import { TabContainer } from '../../../../widgets/tab';
import { ZTabs } from '../../../../widgets/ztabs';
import update from 'immutability-helper';
import React, { FC, useCallback, useEffect, useReducer, useState } from 'react';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';
import { Tab, TabList, TabPanel } from 'react-tabs';
import { Section } from 'src/components/widgets/section';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { ContainerSmall } from '../../containers';
import { useProductConfigurationState } from '../../state';
import SelectedOptionValue from '../../state/selectedOptionValue';
import { variantsQuery$data } from './__generated__/variantsQuery.graphql';
import { MerchantProductVariantImageMatch } from './helpers';

const Row = styled.div`
    padding: 10px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
`;

const OptionRow = styled(Row)`
    border: 1px ${(props) => props.theme.app.primary_light} dashed;
    border-radius: 3px;
    background-color: white;
`;

interface SelectAction {
    type: 'SELECT';
    optionId: string;
    valuesIds: string[];
}

interface DeselectAction {
    type: 'DESELECT';
    optionId: string;
    valuesIds: string[];
}

type ActionType = SelectAction | DeselectAction;

// State is a map where key is the option and value is a set of selected values
const OptionsListReducer = (prevState: Map<string, Set<string>>, action: ActionType) => {
    // Initialized the list of selected values for this option if not exist
    let optionValues = prevState.get(action.optionId)!;
    if (!optionValues) {
        optionValues = new Set<string>();
        prevState = update(prevState, { $add: [[action.optionId, optionValues]] });
    }

    switch (action.type) {
        case 'SELECT':
            optionValues = update(optionValues, { $add: action.valuesIds.filter((x) => !optionValues.has(x)) });
            break;
        case 'DESELECT':
            optionValues = update(optionValues, { $remove: action.valuesIds });
            break;
    }

    prevState = update(prevState, { [action.optionId]: { $set: optionValues } });
    return prevState;
};

interface OptionsListProps {
    onAddVariantClick: (
        selectedOptionsValues: SelectedOptionValue[],
        merchantProductVariantImageMatches: MerchantProductVariantImageMatch[] | null
    ) => void | Promise<void>;
    onCancelClick: () => void;
    options: variantsQuery$data['merchantProductOptions'];
}

const OptionsList: FC<OptionsListProps> = ({ onAddVariantClick, onCancelClick, options }) => {
    const [allSelectedValues, dispatch] = useReducer(OptionsListReducer, new Map<string, Set<string>>());
    const [selectedOptionId, setSelectedOptionId] = useState<string | null>(options.length > 0 ? options[0].optionID : null);

    const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);
    const { showDialog, closeDialog } = useDialogManager();

    const [productVariantImageMatches, setProductVariantImageMatches] = useState<MerchantProductVariantImageMatch[] | null>(null);
    const [productVariantImageMatchesInFlight, setproductVariantImageMatchesInFlight] = useState(false);
    const [pendingAddVariantAction, setPendingAddVariantAction] = useState<SelectedOptionValue[] | null>(null);
    const [isAddingVariants, setIsAddingVariants] = useState(false);

    const getSelectedOption = useCallback(() => {
        return options.find((x) => x.optionID == selectedOptionId);
    }, [selectedOptionId]);

    const isAllSelected = useCallback(() => {
        const selectedOption = getSelectedOption();
        if (selectedOption) {
            const selectedOptionValues = allSelectedValues.get(selectedOption.optionID);
            return (selectedOptionValues && !selectedOption.values.some((x) => !selectedOptionValues.has(x.valueID))) || false;
        } else {
            return false;
        }
    }, [allSelectedValues, selectedOptionId]);

    const isOptionValueSelected = (optionId: string, valueId: string) => {
        const optionValues = allSelectedValues.get(optionId);
        return (optionValues && optionValues.has(valueId)) || false;
    };

    const handleSelectAllChange = () => {
        const selectedOption = getSelectedOption();
        if (selectedOption) {
            if (isAllSelected()) {
                dispatch({
                    type: 'DESELECT',
                    optionId: selectedOption.optionID,
                    valuesIds: selectedOption.values.map((x) => x.valueID) ?? []
                });
            } else {
                dispatch({
                    type: 'SELECT',
                    optionId: selectedOption.optionID,
                    valuesIds: selectedOption.values.map((x) => x.valueID) ?? []
                });
            }
        }
    };

    const handleOptionChange = (value: { valueID: string }) => {
        if (selectedOptionId) {
            dispatch({
                type: isOptionValueSelected(selectedOptionId, value.valueID) ? 'DESELECT' : 'SELECT',
                optionId: selectedOptionId,
                valuesIds: [value.valueID]
            });
        }
    };
    const handleAddVariantClick = async () => {
        const selectedOptionsValues: SelectedOptionValue[] = [];

        options.forEach((option) => {
            option.values.forEach((value) => {
                if (isOptionValueSelected(option.optionID, value.valueID)) {
                    selectedOptionsValues.push({
                        Id: option.optionID,
                        Label: option.label,
                        Value: {
                            Id: value.valueID,
                            Label: value.label
                        }
                    });
                }
            });
        });

        if (productVariantImageMatches === null && productVariantImageMatchesInFlight && isAllSelected()) {
            setPendingAddVariantAction(selectedOptionsValues);
            showDialog(
                'waitForImagesDialog',
                <QuestionDialog
                    title={T._('Image Matching In Progress', 'Admin')}
                    message={T._(
                        'We are matching images to your product variants. This will proceed automatically once complete. You can cancel this process.',
                        'Admin'
                    )}
                    buttonNoLabel={T._('Cancel and continue without getting the images of the variants from the store', 'Admin')}
                    buttonYesLabel={T._('Wait for images', 'Admin')}
                    onYesClick={() => {
                        closeDialog('waitForImagesDialog');
                    }}
                    onNoClick={() => {
                        setPendingAddVariantAction(null);
                        setproductVariantImageMatchesInFlight(false);
                        closeDialog('waitForImagesDialog');
                    }}
                />
            );
        } else {
            setIsAddingVariants(true);
            try {
                await onAddVariantClick(selectedOptionsValues, productVariantImageMatches);
            } finally {
                setIsAddingVariants(false);
            }
        }
    };

    const { state } = useProductConfigurationState();
    const relayEnvironment = useRelayEnvironment();
    useEffect(() => {
        if (pendingAddVariantAction && !productVariantImageMatchesInFlight) {
            setIsAddingVariants(true);
            Promise.resolve(onAddVariantClick(pendingAddVariantAction, productVariantImageMatches)).finally(() => setIsAddingVariants(false));
            closeDialog('waitForImagesDialog');
            setPendingAddVariantAction(null);
        }
    }, [pendingAddVariantAction, productVariantImageMatchesInFlight, productVariantImageMatches, onAddVariantClick, closeDialog, setPendingAddVariantAction]);
    useEffect(() => {
        setproductVariantImageMatchesInFlight(true);
        fetchQuery(
            relayEnvironment,
            graphql`
                query addVariantOptionsListQuery($sku: String!, $salesChannelID: ID!) {
                    productVariantImageMatches(sku: $sku, salesChannelID: $salesChannelID) {
                        imageUrl
                        selections {
                            optionID
                            label
                            value {
                                id
                                label
                            }
                        }
                    }
                }
            `,
            { sku: state.product.code || '', salesChannelID: state.salesChannelID }
        )
            .toPromise()
            .then((data) => {
                const typedData = data as { productVariantImageMatches?: MerchantProductVariantImageMatch[] };
                setProductVariantImageMatches(typedData.productVariantImageMatches || []);
                setproductVariantImageMatchesInFlight(false);
            })
            .catch((error) => {
                console.error('Error fetching product variant image matches:', error);
                setproductVariantImageMatchesInFlight(false);
            });
    }, [relayEnvironment, state.product.code, state.salesChannelID]);

    return (
        <>
            <ZTabs
                selectedIndex={selectedOptionIndex}
                onSelect={(i) => {
                    setSelectedOptionIndex(i);
                    setSelectedOptionId(options[i].optionID);
                }}
            >
                <TabList>
                    {options.map((x) => (
                        <Tab key={x.optionID}>{x.label}</Tab>
                    ))}
                </TabList>

                {options.map((opt) => (
                    <TabPanel key={opt.optionID}>
                        <Section
                            description={
                                <>
                                    <strong>Select the attributes you want to import</strong>
                                </>
                            }
                        >
                            <ContainerSmall
                                css={`
                                    margin-bottom: 40px;
                                `}
                            >
                                <TabContainer>
                                    <Row key={selectedOptionId || ''}>
                                        <Checkbox checked={isAllSelected()} onChange={handleSelectAllChange} label={T._('Select all', 'Admin')} />
                                    </Row>
                                    {selectedOptionId &&
                                        getSelectedOption()?.values.map((value, idx) => {
                                            return (
                                                <OptionRow key={value.valueID + '-' + idx}>
                                                    <Checkbox
                                                        checked={isOptionValueSelected(selectedOptionId, value.valueID)}
                                                        onChange={() => handleOptionChange(value)}
                                                        label={value.label}
                                                    />
                                                </OptionRow>
                                            );
                                        })}
                                </TabContainer>
                            </ContainerSmall>
                        </Section>
                    </TabPanel>
                ))}
            </ZTabs>
            <PageFooterButtons $fixed={true}>
                <BasicButton onClick={onCancelClick} disabled={isAddingVariants}>
                    {T._(`Cancel`, 'Admin')}
                </BasicButton>
                <Button onClick={handleAddVariantClick} disabled={isAddingVariants}>
                    {isAddingVariants ? T._('Adding variations...', 'Admin') : T._('Add variations', 'Admin')}
                </Button>
            </PageFooterButtons>
        </>
    );
};

export default OptionsList;
